// frontend/src/pages/admin/AdminInspectionForm.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { vehiclesApi } from '../../api/vehicles';
import { inspectionsApi } from '../../api/inspections';
import { aiApi } from '../../api/ai';
import { FaSyncAlt } from 'react-icons/fa';
import { 
    FaArrowLeft, FaCheckCircle, FaExclamationTriangle, FaTimesCircle, 
    FaSave, FaSpinner, FaCar, FaMagic, FaTimes, FaHistory, FaRobot 
} from 'react-icons/fa';

const SECTIONS = [
    { id: 'engine', label: 'Engine' },
    { id: 'transmission', label: 'Transmission' },
    { id: 'electrical', label: 'Electrical System' },
    { id: 'suspension', label: 'Suspension & Steering' },
    { id: 'brakes', label: 'Brake System' },
    { id: 'body_frame', label: 'Body & Frame' },
    { id: 'tires_wheels', label: 'Tires & Wheels' },
    { id: 'interior', label: 'Interior' }
];

function AdminInspectionForm() {
    const { vehicleId } = useParams();
    const navigate = useNavigate();
    const [vehicle, setVehicle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [existingReportId, setExistingReportId] = useState(null);

    // AI State
    const [generatingSection, setGeneratingSection] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatedOptions, setGeneratedOptions] = useState([]);
    const [showOptionsModal, setShowOptionsModal] = useState(false);

    // Form State
    const [formData, setFormData] = useState({
        inspector_verdict: '',
        // Initialize all sections
        ...SECTIONS.reduce((acc, sec) => ({ ...acc, [sec.id]: { status: 'good', notes: '' } }), {})
    });

    useEffect(() => {
        const init = async () => {
            try {
                // 1. Fetch Vehicle
                const vehicleData = await vehiclesApi.getVehicleById(vehicleId);
                setVehicle(vehicleData);

                // 2. Fetch Existing Report (if any)
                try {
                    const reportData = await inspectionsApi.getVehicleReport(vehicleId);
                    if (reportData) {
                        setExistingReportId(reportData.id);
                        
                        // Pre-fill Form
                        const newFormData = {
                            inspector_verdict: reportData.inspector_verdict || '',
                        };
                        
                        SECTIONS.forEach(sec => {
                            if (reportData[sec.id]) {
                                newFormData[sec.id] = {
                                    status: reportData[sec.id].status || 'good',
                                    notes: reportData[sec.id].notes || ''
                                };
                            } else {
                                newFormData[sec.id] = { status: 'good', notes: '' };
                            }
                        });
                        
                        setFormData(newFormData);
                    }
                } catch (e) {
                    // No report found, ignore (start fresh)
                }

            } catch (err) {
                alert("Failed to load vehicle data.");
                navigate('/admin/vehicles');
            } finally {
                setLoading(false);
            }
        };
        init();
    }, [vehicleId, navigate]);

    const handleStatusChange = (sectionId, status) => {
        setFormData(prev => ({
            ...prev,
            [sectionId]: { ...prev[sectionId], status }
        }));
    };

    const handleNotesChange = (sectionId, notes) => {
        setFormData(prev => ({
            ...prev,
            [sectionId]: { ...prev[sectionId], notes }
        }));
    };

    // --- AI GENERATION LOGIC (DEBUGGED) ---
    const handleGenerateNotes = async (sectionId, keepModal = false) => {
        if (!vehicle) return;

        setGeneratingSection(sectionId);
        setIsGenerating(true);
        if (!keepModal) setGeneratedOptions([]);
        
        try {
            const vehicleInfo = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
            let payload = {
                section: sectionId,
                vehicle_info: vehicleInfo
            };

            if (sectionId === 'verdict') {
                payload.current_form_data = formData;
            } else {
                payload.status = formData[sectionId].status;
            }
            
            console.log("[DEBUG] Sending Payload to AI:", payload);

            const response = await aiApi.generateInspectionNotes(payload);
            console.log("[DEBUG] Received AI Response:", response);
            
            if (response.options && response.options.length > 0) {
                setGeneratedOptions(response.options);
                setShowOptionsModal(true);
            } else {
                console.warn("[DEBUG] AI returned empty options.");
                alert("AI could not generate notes at this time. Please check the backend logs.");
                setGeneratingSection(null);
            }

        } catch (err) {
            console.error("[DEBUG] AI Generation Error:", err);
            alert("Failed to generate notes. Please check your connection.");
            setGeneratingSection(null);
        } finally {
            setIsGenerating(false);
        }
    };

    const handleSelectOption = (option) => {
        if (generatingSection === 'verdict') {
            setFormData(prev => ({ ...prev, inspector_verdict: option }));
        } else if (generatingSection) {
            handleNotesChange(generatingSection, option);
        }
        
        setShowOptionsModal(false);
        setGeneratedOptions([]);
        setGeneratingSection(null);
    };

    const handleCloseModal = () => {
        setShowOptionsModal(false);
        setGeneratedOptions([]);
        setGeneratingSection(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.inspector_verdict.trim()) {
            alert("Please provide an overall verdict.");
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                vehicle_id: vehicleId,
                ...formData
            };
            await inspectionsApi.createReport(payload);
            alert("Inspection Report Saved Successfully!");
            navigate('/admin/vehicles');
        } catch (err) {
            alert("Failed to submit report.");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="flex justify-center items-center h-screen"><FaSpinner className="animate-spin text-4xl text-primary" /></div>;

    return (
        <div className="bg-gray-50 min-h-screen p-6">
            <div className="max-w-4xl mx-auto">
                
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                        <Link to="/admin/vehicles" className="text-gray-500 hover:text-primary">
                            <FaArrowLeft /> Back
                        </Link>
                        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                            <FaCar /> Inspecting: {vehicle.year} {vehicle.make} {vehicle.model}
                        </h1>
                    </div>
                    {existingReportId && (
                        <span className="bg-blue-100 text-blue-800 text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                            <FaHistory /> Editing Existing Report
                        </span>
                    )}
                </div>

                {/* --- AI TOOLKIT --- */}
                <div className="bg-purple-50 border-l-4 border-purple-500 p-4 mb-6 rounded-r-lg shadow-sm flex items-start gap-3">
                    <FaRobot className="text-purple-500 text-xl mt-1 flex-shrink-0" />
                    <div>
                        <h4 className="font-bold text-purple-800 text-sm">AI Inspection Assistant Toolkit</h4>
                        <p className="text-xs text-purple-700 mt-1">
                            Click the <FaMagic className="inline" /> button next to any notes field to generate 3 professional, technical descriptions based on the selected status (Good, Fair, or Poor).
                        </p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    
                    {/* Sections Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {SECTIONS.map(section => (
                            <div key={section.id} className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
                                <h3 className="font-bold text-gray-800 mb-3 border-b pb-2">{section.label}</h3>
                                
                                {/* Status Radios */}
                                <div className="flex gap-4 mb-3">
                                    <label className={`flex-1 cursor-pointer p-2 rounded border text-center transition-colors ${formData[section.id].status === 'good' ? 'bg-green-100 border-green-500 text-green-800 font-bold' : 'border-gray-200 hover:bg-gray-50'}`}>
                                        <input 
                                            type="radio" 
                                            name={`${section.id}_status`} 
                                            className="hidden"
                                            checked={formData[section.id].status === 'good'}
                                            onChange={() => handleStatusChange(section.id, 'good')}
                                        />
                                        <FaCheckCircle className="inline mr-1" /> Good
                                    </label>
                                    
                                    <label className={`flex-1 cursor-pointer p-2 rounded border text-center transition-colors ${formData[section.id].status === 'fair' ? 'bg-yellow-100 border-yellow-500 text-yellow-800 font-bold' : 'border-gray-200 hover:bg-gray-50'}`}>
                                        <input 
                                            type="radio" 
                                            name={`${section.id}_status`} 
                                            className="hidden"
                                            checked={formData[section.id].status === 'fair'}
                                            onChange={() => handleStatusChange(section.id, 'fair')}
                                        />
                                        <FaExclamationTriangle className="inline mr-1" /> Fair
                                    </label>
                                    
                                    <label className={`flex-1 cursor-pointer p-2 rounded border text-center transition-colors ${formData[section.id].status === 'poor' ? 'bg-red-100 border-red-500 text-red-800 font-bold' : 'border-gray-200 hover:bg-gray-50'}`}>
                                        <input 
                                            type="radio" 
                                            name={`${section.id}_status`} 
                                            className="hidden"
                                            checked={formData[section.id].status === 'poor'}
                                            onChange={() => handleStatusChange(section.id, 'poor')}
                                        />
                                        <FaTimesCircle className="inline mr-1" /> Poor
                                    </label>
                                </div>

                                {/* Notes with AI Button */}
                                <div className="relative">
                                    <textarea 
                                        placeholder={`Notes on ${section.label.toLowerCase()}...`}
                                        value={formData[section.id].notes}
                                        onChange={(e) => handleNotesChange(section.id, e.target.value)}
                                        className="w-full p-2 border rounded text-sm focus:ring-2 focus:ring-primary outline-none pr-10"
                                        rows="2"
                                    ></textarea>
                                    <button
                                        type="button"
                                        onClick={() => handleGenerateNotes(section.id)}
                                        disabled={isGenerating && generatingSection === section.id}
                                        className="absolute top-2 right-2 text-purple-600 hover:text-purple-800 p-1 rounded hover:bg-purple-50 transition-colors"
                                        title="Generate AI Notes"
                                    >
                                        {isGenerating && generatingSection === section.id ? <FaSpinner className="animate-spin" /> : <FaMagic />}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Verdict */}
                    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                        <div className="flex justify-between items-center mb-3">
                            <h3 className="font-bold text-gray-800">Inspector's Verdict</h3>
                            <button
                                type="button"
                                onClick={() => handleGenerateNotes('verdict')}
                                disabled={isGenerating && generatingSection === 'verdict'}
                                className="text-sm bg-purple-100 text-purple-700 px-3 py-1 rounded font-bold flex items-center gap-2 hover:bg-purple-200 transition-colors"
                            >
                                {isGenerating && generatingSection === 'verdict' ? <FaSpinner className="animate-spin" /> : <FaMagic />} 
                                Generate Verdict
                            </button>
                        </div>
                        <textarea 
                            required
                            value={formData.inspector_verdict}
                            onChange={(e) => setFormData({...formData, inspector_verdict: e.target.value})}
                            className="w-full p-3 border rounded focus:ring-2 focus:ring-primary outline-none"
                            rows="4"
                            placeholder="Overall summary of the vehicle's condition. This will be visible to buyers."
                        ></textarea>
                    </div>

                    <div className="flex justify-end">
                        <button 
                            type="submit" 
                            disabled={submitting}
                            className="bg-primary hover:bg-primary-dark text-white font-bold py-3 px-8 rounded shadow-lg flex items-center gap-2 transition-transform transform hover:-translate-y-1"
                        >
                            {submitting ? <FaSpinner className="animate-spin" /> : <FaSave />} 
                            {existingReportId ? 'Update Report' : 'Submit Report'}
                        </button>
                    </div>

                </form>
            </div>

            {/* --- AI OPTIONS MODAL --- */}
            {showOptionsModal && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg animate-fadeInUp">
                        <div className="flex justify-between items-center p-4 border-b bg-purple-50 rounded-t-lg">
                            <h3 className="font-bold text-purple-800 flex items-center gap-2">
                                <FaMagic /> AI Suggestions
                            </h3>
                            <button onClick={handleCloseModal} className="text-gray-500 hover:text-gray-700">
                                <FaTimes />
                            </button>
                        </div>
                        <div className="p-4 space-y-3">
                            <p className="text-sm text-gray-600 mb-2">Select the best note for this section:</p>
                            {isGenerating ? (
                                <div className="flex items-center justify-center gap-2 text-purple-700 py-6">
                                    <FaSpinner className="animate-spin" /> Writing fresh suggestions...
                                </div>
                            ) : (
                                generatedOptions.map((option, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => handleSelectOption(option)}
                                        className="w-full text-left p-3 border rounded hover:bg-purple-50 hover:border-purple-300 transition-colors text-sm text-gray-800"
                                    >
                                        {option}
                                    </button>
                                ))
                            )}
                        </div>
                        <div className="p-4 border-t bg-gray-50 rounded-b-lg flex items-center justify-between gap-2">
                            <button
                                onClick={() => handleGenerateNotes(generatingSection, true)}
                                disabled={isGenerating}
                                className="inline-flex items-center gap-1.5 text-sm font-bold text-purple-700 border border-purple-300 hover:bg-purple-100 disabled:opacity-50 px-3 py-1.5 rounded-full"
                            >
                                {isGenerating ? <FaSpinner className="animate-spin" /> : <FaSyncAlt />} Regenerate
                            </button>
                            <button onClick={handleCloseModal} className="text-gray-600 text-sm hover:underline">Cancel</button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}

export default AdminInspectionForm;